import React from "react";
import PhoneInputLib from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { FormHelperText, Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { isValidPhoneNumber } from "react-phone-number-input";

// Custom styled container that matches the Input component styling
const StyledPhoneInputContainer = styled(Box)(({ contactPage }) => ({
  position: "relative",

  "& .PhoneInput": {
    position: "relative",
    display: "flex",
    alignItems: "center",
    height: contactPage ? "52px" : "56px", // Match contact form input height
    backgroundColor: contactPage ? "#faf7f5" : "#ffffff", // Match contact form background
    borderRadius: "8px",
    border: contactPage ? "1px solid #ddd" : "1px solid #e0e0e0", // Match contact form border
    boxShadow: contactPage ? "0 2px 4px rgba(0,0,0,0.05)" : "none", // Match contact form shadow
    transition: "all 0.3s ease",
    overflow: "hidden",

    "&:focus-within": {
      backgroundColor: contactPage ? "#faf7f5" : "#ffffff",
      borderColor: contactPage ? "#c84653" : "#bdbdbd", // Match contact form focus color
      boxShadow: contactPage ? "0 0 0 3px rgba(200, 70, 83, 0.2)" : "none", // Match contact form focus shadow
    },
  },

  "& .PhoneInputCountry": {
    padding: contactPage ? "0 12px 0 20px" : "0 8px 0 16px", // Match contact form padding
    display: "flex",
    alignItems: "center",
    height: "100%",
    borderRight: `none`, // Lighter border
    backgroundColor: "transparent",
  },

  "& .PhoneInputCountryIcon": {
    width: "20px", // Slightly smaller
    height: "15px",
    marginRight: "6px", // Reduced margin
    borderRadius: "2px",
  },

  "& .PhoneInputCountrySelect": {
    background: "transparent",
    border: "none",
    outline: "none",
    cursor: "pointer",
    fontSize: "14px", // Smaller font size
    fontWeight: 400, // Match Input font weight
    color: "#666666", // Match Input color scheme
    padding: "2px",
    borderRadius: "4px",

    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
    },
  },

  "& .PhoneInputInput": {
    flex: "1",
    border: "none",
    outline: "none",
    fontSize: contactPage ? "16px" : "16px", // Match contact form font size
    fontWeight: 400, // Match Input font weight
    color: "#333333",
    backgroundColor: "transparent",
    height: "100%",
    padding: contactPage ? "16px 20px 16px 0" : "0", // Match contact form padding

    "&::placeholder": {
      color: "#666666",
      opacity: 1,
    },
  },
}));

// Label component that matches Input component styling exactly
const StyledLabel = styled(Box)(
  ({ theme, error, focused, hasValue, contactPage }) => ({
    position: "absolute",
    left: "15px", // Match Input label positioning
    top: focused || hasValue ? "-9px" : contactPage ? "16px" : "19px", // Match Input label positioning based on context
    transform: focused || hasValue ? "scale(1)" : "scale(1)", // Remove scaling to match Input
    fontSize: focused || hasValue ? "12px" : "16px", // Match Input font sizes
    fontWeight: 400, // Match Input font weight
    color: error ? theme.palette.error.main : "#666666", // Match Input color scheme
    backgroundColor:
      focused || hasValue
        ? contactPage
          ? "#faf7f5"
          : "#ffffff"
        : contactPage
        ? "#faf7f5"
        : "#ffffff",
    padding: focused || hasValue ? "0 4px" : "0px 16px", // Match Input padding
    transition: "all 0.3s ease",
    pointerEvents: "none",
    zIndex: 1,
    transformOrigin: "left top",
  })
);

const CustomPhoneInput = React.forwardRef(
  (
    {
      value,
      onChange,
      error: externalError,
      label,
      placeholder = "",
      defaultCountry = "FR",
      countries,
      sx,
      required = false,
      validateOnBlur = true,
      setPhoneError,
      contactPage,
      ...props
    },
    ref
  ) => {
    const [focused, setFocused] = React.useState(false);
    const [internalError, setInternalError] = React.useState("");
    const [touched, setTouched] = React.useState(false);

    const hasValue = Boolean(value);
    const error = externalError || internalError;

    const validatePhone = (phoneValue) => {
      if (!phoneValue && required) {
        return "Le numéro de téléphone est requis";
      }

      if (phoneValue && !isValidPhoneNumber(phoneValue)) {
        return "Le numéro de téléphone n'est pas valide";
      }

      return "";
    };

    const handleFocus = () => setFocused(true);

    const handleBlur = () => {
      setFocused(false);
      setTouched(true);

      if (validateOnBlur) {
        const errorMessage = validatePhone(value);
        setInternalError(errorMessage);
      }
    };

    const handleChange = (phoneValue) => {
      // Clear error when user starts typing
      if (touched && internalError) {
        setInternalError("");
      }

      // Call the external onChange
      if (onChange) {
        onChange(phoneValue);
      }

      // Validate immediately if the field was previously touched and had an error
      if (touched && validateOnBlur) {
        const errorMessage = validatePhone(phoneValue);
        setInternalError(errorMessage);

        if (setPhoneError) {
          setPhoneError(!errorMessage);
        }
      }
    };

    // Expose validation method via ref
    React.useImperativeHandle(ref, () => ({
      validate: () => {
        const errorMessage = validatePhone(value);
        setInternalError(errorMessage);
        setTouched(true);
        if (setPhoneError) {
          setPhoneError(!errorMessage);
        }
        return !errorMessage;
      },
      clearError: () => {
        setInternalError("");
        if (setPhoneError) {
          setPhoneError(true);
        }
      },
    }));

    return (
      <Box
        sx={{ position: "relative", width: "100%", minWidth: "100px", ...sx }}
      >
        <StyledPhoneInputContainer error={!!error} contactPage={contactPage}>
          {label && (
            <StyledLabel
              error={!!error}
              focused={focused}
              hasValue={hasValue}
              contactPage={contactPage}
            >
              {label}
              {required && " *"}
            </StyledLabel>
          )}

          <PhoneInputLib
            ref={ref}
            value={value}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            defaultCountry={defaultCountry}
            countries={countries}
            placeholder={placeholder}
            international
            countryCallingCodeEditable={false}
            {...props}
          />
        </StyledPhoneInputContainer>

        {error && (
          <FormHelperText error sx={{ mt: 0.5, mx: 1.75, fontSize: "12px" }}>
            {error}
          </FormHelperText>
        )}
      </Box>
    );
  }
);

CustomPhoneInput.displayName = "CustomPhoneInput";

export default CustomPhoneInput;
